import * as React from 'react'
import { MoonLoader } from 'react-spinners'
import LocationSearch from '../../containers/trips/LocationSearch'
import TripServiceList from '../../containers/trips/services/TripServiceList'
import RequestTimeSettings from '../../containers/trips/RequestTimeSettings'
import RecurringTripModal from '../../containers/trips/RecurringTripModal'
import {
  IAvailabilityDTO,
  IRiderObject,
  ITripRequestState,
  IOrgObj,
  ITripPassengerDetail,
  IServicePricingType,
  ILocationV2
} from '../../types'
import UIMessages from '../../utils/ui-messages'
import FavoriteTripIcon from '../icons/ui/FavoriteTripIcon'
import { Route, RouteComponentProps, Switch } from 'react-router'
import CharacterCounter from '../ui/CharacterCounter'
import SetTripPassengerModal from 'src/containers/trips/modal/SetTripPassengerModal'
import moment from 'moment'
import { isValidCoordinates } from 'src/utils/validators'

export interface ICreateTripProps extends RouteComponentProps<{ riderId: string }> {
  orgId: string
  organization: IOrgObj
  riderId: string
  rider: IRiderObject
  tripRequest: ITripRequestState
  tripInfo: IAvailabilityDTO
  polyline: string | null
  riderCount: number
  additionalRiders: number
  pricingType: IServicePricingType[]
  selectOrg: (orgId: string) => void
  selectRider: (rider: any) => void
  setCurrentTripStage: () => void
  getAvailableProducts: (riderId: string, tripInfo: any) => Promise<any>
  updateTripNotes: (notes: string) => void
  updateTripConfirmationNumber: (confirmationNumber: string) => void
  updateTripProviderOneNumber: (providerOneNumber: string) => void
  updateTripRequester: (requesterName: string) => void
  updateRiderCount: (count: number) => void
  setTripPassengerDetails: (passengerDetails: ITripPassengerDetail) => void
  setScheduledTime: (time: string, type: string) => Promise<any>
}

interface ICreateTripState {
  loading: boolean
  availableProductSearchComplete: boolean
  riderCount: number
  willCall: boolean
  passengerDetails: ITripPassengerDetail
  scheduledReturn: boolean
  lastScheduledPickupTime: string
}

interface ICreateTripContext {
  riderId?: string
}

const CHARACTER_LIMIT = 250

export const CreateTripContext = React.createContext<ICreateTripContext>({})

const Favorite = (props: any) => (
  <div>
    <p className='px2 uppercase h5 '>Favorite</p>
    {props.favoriteTrips.map((item: any) => (
      <div className='flex px2 cursor--pointer mb1' key={item.id}>
        <FavoriteTripIcon />
        <div className='pl3'>
          <div className='h5 font-dark-grey'>{item.location.pickUpFullAddress}</div>
        </div>
      </div>
    ))}
  </div>
)

class CreateTrip extends React.Component<ICreateTripProps, ICreateTripState> {
  public state = {
    loading: false,
    availableProductSearchComplete: false,
    riderCount: 1,
    willCall: false,
    passengerDetails: this.props.tripRequest.request.passengerDetails!,
    scheduledReturn: false,
    lastScheduledPickupTime: moment().format('YYYY/MM/DD HH:mm')
  }

  public async componentDidUpdate(nextProps: ICreateTripProps) {
    const {
      pickupLocation: prevPickupLocation,
      dropoffLocation: prevDropoffLocation,
      scheduledPickupTime: prevScheduledPickupTime,
      scheduledDropoffTime: prevScheduledDropoffTime,
      leaveAtDateTime: prevLeaveAtDateTime,
      arriveByDateTime: prevArriveByDateTime,
      additionalStop: prevAdditonalStop
    } = nextProps.tripRequest.request

    const { tripRequest, tripInfo } = this.props
    const {
      pickupLocation,
      dropoffLocation,
      scheduledDropoffTime,
      scheduledPickupTime,
      leaveAtDateTime,
      arriveByDateTime,
      additionalStop
    } = tripRequest.request

    if (tripRequest && nextProps.rider && nextProps.rider.firstName) {
      const rider = tripRequest.request.rider
      if (!rider || (!rider.firstName && nextProps.rider.firstName.length)) {
        const notes = tripRequest.request.notes
          ? tripRequest.request.notes
          : nextProps.rider.note
          ? nextProps.rider.note
          : ''
        const confirmationNumber = tripRequest.request.confirmationNumber ? tripRequest.request.confirmationNumber : ''
        const providerOneNumber = tripRequest.request.providerOneNumber ? tripRequest.request.providerOneNumber : ''
        this.props.selectRider(nextProps.rider)
        this.props.selectOrg(nextProps.orgId)
        this.props.updateTripNotes(notes)
        this.props.updateTripConfirmationNumber(confirmationNumber)
        this.props.updateTripProviderOneNumber(providerOneNumber)
      }
    }

    if (
      tripInfo &&
      tripInfo.origin &&
      tripInfo.origin.notes &&
      JSON.stringify(tripInfo.origin) !== JSON.stringify(nextProps.tripInfo.origin)
    ) {
      const newNote = tripInfo.notes + (tripInfo.notes ? '\n' : '') + 'PO:' + tripInfo.origin.notes
      this.props.updateTripNotes(newNote)
    }

    if (
      tripRequest &&
      tripRequest.request &&
      tripRequest.request.additionalStop &&
      JSON.stringify(tripRequest.request.additionalStop) !==
        JSON.stringify(nextProps.tripRequest.request.additionalStop)
    ) {
      let allStopNotes = ''
      tripRequest.request.additionalStop.forEach((stop, index) => {
        if (stop.notes !== undefined && stop.notes !== 'undefined' && stop.notes !== null && stop.notes !== '') {
          const tempTripInfoNotes = tripInfo.notes ? tripInfo.notes : ''
          if (!tempTripInfoNotes.includes('STOP ' + stop.index)) {
            allStopNotes = 'STOP ' + stop.index + ':' + stop.notes
          }
        }
      })
      const newNote = tripInfo.notes + (tripInfo.notes ? '\n' : '') + allStopNotes
      this.props.updateTripNotes(newNote)
    }

    if (
      tripInfo &&
      tripInfo.destination &&
      tripInfo.destination.notes &&
      JSON.stringify({
        ...tripInfo.destination,
        phoneNumber: ''
      }) !==
        JSON.stringify({
          ...nextProps.tripInfo.destination,
          phoneNumber: ''
        })
    ) {
      const newNote = tripInfo.notes + (tripInfo.notes ? '\n' : '') + 'DO:' + tripInfo.destination.notes
      this.props.updateTripNotes(newNote)
    }

    const formattedPrevPickup = moment(prevScheduledPickupTime).format('YYYY/MM/DD HH:mm')
    const formattedCurrPickup = moment(scheduledPickupTime).format('YYYY/MM/DD HH:mm')
    const prevAdditionalStopJSON = JSON.stringify(prevAdditonalStop)
    const additionalStopJSON = JSON.stringify(additionalStop)

    const formattedPrevLeaveAt = moment(prevLeaveAtDateTime).format('YYYY/MM/DD HH:mm')
    const formattedCurrLeaveAt = moment(leaveAtDateTime).format('YYYY/MM/DD HH:mm')
    const formattedPrevArriveBy = moment(prevArriveByDateTime).format('YYYY/MM/DD HH:mm')
    const formattedCurrArriveBy = moment(arriveByDateTime).format('YYYY/MM/DD HH:mm')

    if (
      prevPickupLocation !== pickupLocation ||
      prevDropoffLocation !== dropoffLocation ||
      formattedPrevPickup !== formattedCurrPickup ||
      prevScheduledDropoffTime !== scheduledDropoffTime ||
      prevAdditionalStopJSON !== additionalStopJSON ||
      formattedPrevLeaveAt !== formattedCurrLeaveAt ||
      formattedPrevArriveBy !== formattedCurrArriveBy
    ) {
      if (!this.inValidCoordinates(pickupLocation, dropoffLocation)) {
        try {
          await this.getAvailableProducts()
        } catch (error) {
          console.error('Error in componentDidUpdate while getting available products:', error)
        }
      }
    }
  }
  public inValidCoordinates(origin: ILocationV2, destination: ILocationV2) {
    return origin.latitude === 0 || origin.longitude === 0 || destination.latitude === 0 || destination.longitude === 0
  }
  public componentDidMount() {
    const { tripRequest } = this.props
    const notes = tripRequest.request.notes
      ? tripRequest.request.notes
      : this.props.rider
      ? this.props.rider.note || ''
      : ''
    const confirmationNumber = tripRequest.request.confirmationNumber ? tripRequest.request.confirmationNumber : ''
    const providerOneNumber = tripRequest.request.providerOneNumber ? tripRequest.request.providerOneNumber : ''
    this.props.selectOrg(this.props.orgId)
    this.props.updateTripNotes(notes)

    if (
      (tripRequest && tripRequest.request.scheduledPickupTime) ||
      (tripRequest && tripRequest.request.leaveAtDateTime) ||
      (tripRequest && tripRequest.request.arriveByDateTime)
    ) {
      this.getAvailableProducts()
    }
  }

  public componentWillUnmount() {
    this.props.updateTripNotes('')
  }
  public render() {
    const { tripInfo, riderId, tripRequest } = this.props
    const { recurringDropoffTime, recurringPickupTime, timesToRecur, providerOneNumber } = tripRequest.request
    const { loading, availableProductSearchComplete } = this.state
    const tripDetailsComplete = tripInfo && tripInfo.origin.address.length && tripInfo.destination.address.length
    let mainContent = null
    const numOfChar = tripInfo.notes ? CHARACTER_LIMIT - tripInfo.notes.length : CHARACTER_LIMIT
    const { crontabExp, runForever } = this.displayRecurringTripDetails()
    if (tripDetailsComplete && !loading && !availableProductSearchComplete) {
      mainContent = (
        <div>
          <div className='flex mt2 items-center justify-center'>
            <button onClick={() => this.getAvailableProducts()} className='btn btn-primary'>
              See what's available
            </button>
          </div>
        </div>
      )
    } else if (tripDetailsComplete && !loading && availableProductSearchComplete) {
      mainContent = (
        <div>
          <TripServiceList riderId={riderId} />
          <div className='flex justify-center mt2'>
            <button onClick={() => this.getAvailableProducts()} className='btn btn-primary'>
              Search again
            </button>
          </div>
        </div>
      )
    }
    const setTripPassengerModal = (props: any) => {
      return <SetTripPassengerModal {...props} />
    }

    return (
      <section>
        <Switch>
          <Route path={`${this.props.match.path}/passengers`} render={setTripPassengerModal} />
        </Switch>
        <CreateTripContext.Provider value={{ riderId }}>
          <div className='p1 bg#333 trip-pane-content'>
            <LocationSearch
              riderId={this.props.riderId}
              bounds={this.props.organization && this.props.organization.serviceAreaBounds}
            />
            <RequestTimeSettings refreshAvailableProducts={this.getAvailableProducts} />
            {(recurringPickupTime || recurringDropoffTime) && (
              <div>
                <RecurringTripModal />
                <div className='font-grey2 h5'>Days to schedule:</div>
                <div className='font-black h5 mt1'>{this.addSpacesAfterCommas(crontabExp)}</div>
                <div className='font-grey2 h5 mt1'>{UIMessages.trip.recurringTrips.runForever}</div>
                <div className='font-black h5 mt1'>{runForever}</div>
                <div className='font-grey2 h5 mt1'>{UIMessages.trip.recurringTrips.timeToRecur}</div>
                <div className='font-black h5 mt1'>
                  {tripRequest.request.runForever === true ? UIMessages.trip.recurringTrips.doesntEnd : timesToRecur}
                </div>
              </div>
            )}
            {(tripRequest.request.service.name.includes('Special Mobility Services') ||
              tripRequest.request.service.name.includes('People For People')) && (
              <div>
                <div className='ml2'>
                  <label className='m0 font-small font-grey2'>Confirmation Number</label>
                  <input
                    className='provider-confirmation'
                    value={tripInfo.confirmationNumber}
                    name='confirmationNumber'
                    onChange={this.onUpdateTripConfirmationNumber}
                    type='text'
                    maxLength={15}
                  />
                </div>

                <div className='ml2'>
                  <label className='m0 font-small font-grey2'>Provider One Number</label>
                  <input
                    value={tripInfo.providerOneNumber || ''}
                    name='providerOneNumber'
                    onChange={this.onUpdateTripProviderOneNumber}
                    type='text'
                    maxLength={20}
                  />
                </div>

                <div className='ml2'>
                  <label className='m0 font-small font-grey2'>Trip Requester</label>
                  <input
                    value={tripInfo.tripRequester}
                    name='tripRequester'
                    onChange={this.onUpdateTripRequester}
                    type='text'
                    maxLength={30}
                  />
                </div>
              </div>
            )}
            {!!tripDetailsComplete && (
              <React.Fragment>
                <div className='ml2'>
                  <label className='m0 font-small font-grey2'>Notes</label>
                  <textarea
                    rows={6}
                    value={tripInfo.notes || ''}
                    name='notes'
                    placeholder='Trip Notes'
                    onChange={this.onUpdateTripRiderNotes}
                    maxLength={CHARACTER_LIMIT}
                  />
                  <CharacterCounter numOfChar={numOfChar} />
                </div>
              </React.Fragment>
            )}

            <div className='flex items-center justify-center'>
              <MoonLoader sizeUnit={'px'} size={27} color={'#405264'} loading={this.state.loading} />
            </div>
            {mainContent}
          </div>
        </CreateTripContext.Provider>
      </section>
    )
  }

  private isNotScheduled(time?: string) {
    return (
      !time ||
      (moment(time).format('YYYY/MM/DD HH:mm') === moment().format('YYYY/MM/DD HH:mm') ||
        moment(time).isAfter(moment()))
    )
  }

  private getAvailableProducts = async () => {
    const { riderId, tripInfo } = this.props

    if (!isValidCoordinates(tripInfo.origin, tripInfo.destination)) {
      // Prevent requests with invalid coordinate values.
      console.log('Get available products request has been terminated! ', tripInfo)
      return
    }
    this.setState({
      loading: true,
      lastScheduledPickupTime: moment().format('YYYY/MM/DD HH:mm')
    })

    const isNotScheduledPickup = this.isNotScheduled(this.props.tripRequest.request.scheduledPickupTime)
    const isNotScheduledLeaveAt = this.isNotScheduled(this.props.tripRequest.request.leaveAtDateTime)
    const isNotScheduledArriveBy = this.isNotScheduled(this.props.tripRequest.request.arriveByDateTime)

    console.log('Schedule check:', {
      isNotScheduledPickup,
      isNotScheduledLeaveAt,
      isNotScheduledArriveBy
    })

    if (isNotScheduledPickup && isNotScheduledLeaveAt && isNotScheduledArriveBy) {
      try {
        console.log('Calling getAvailableProducts with params:', {
          riderId,
          tripInfo: {
            ...tripInfo,
            displayPublicService: true,
            timezone: moment.tz.guess()
          }
        })

        await this.props.getAvailableProducts(riderId, {
          ...tripInfo,
          displayPublicService: true,
          timezone: moment.tz.guess()
        })

        console.log('getAvailableProducts completed successfully')
        this.setState({
          loading: false,
          availableProductSearchComplete: true
        })
      } catch (error) {
        console.error('Error in getAvailableProducts:', error)
        this.setState({ loading: false })
      }
    } else {
      console.log('Handling scheduled time case')
      const time = moment()
      const req = this.props.tripRequest.request
      const schedType = req.scheduledPickupTime
        ? 'pickup'
        : req.leaveAtDateTime
        ? 'leave-at'
        : req.arriveByDateTime
        ? 'arrive-by'
        : 'pickup'
      this.props.setScheduledTime(time.toISOString(), schedType)
    }
  }

  private onUpdateTripRiderNotes = (e: any) => {
    this.props.updateTripNotes(e.target.value)
  }

  private onUpdateTripConfirmationNumber = (e: any) => {
    this.props.updateTripConfirmationNumber(e.target.value)
  }
  private onUpdateTripProviderOneNumber = (e: any) => {
    if (e.target.value.length === 1 && e.target.value !== 'W') {
      const val = 'W' + e.target.value
      this.props.updateTripProviderOneNumber(val)
    } else {
      this.props.updateTripProviderOneNumber(e.target.value)
    }
  }
  private onUpdateTripRequester = (e: any) => {
    this.props.updateTripRequester(e.target.value)
  }
  private displayRecurringTripDetails = () => {
    const { runForever, crontabExp } = this.props.tripRequest.request
    const crontabArr = crontabExp ? crontabExp.split('*') : []
    const days = crontabArr[crontabArr.length - 1]
    return {
      crontabExp: days,
      runForever: runForever === true ? 'Never' : 'Yes'
    }
  }

  private addSpacesAfterCommas = (input: string): string => {
    if (!input) {
      return ''
    }
    return input.replace(/,/g, ', ')
  }
}

export default CreateTrip



// WEBPACK FOOTER //
// ./src/components/trips/CreateTrip.tsx